import React, { SyntheticEvent } from 'react'

import { safeHtml } from '@library/dom'

import {
  Box,
  Display,
  FlexDirection,
  AlignItems,
  Color,
  FontFamily,
  Icon,
  IconVariant,
  Text,
  ModalButton,
  ButtonColorVariant,
  ButtonSize,
  Spinner,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { CheckoutMessage } from '@library/api-menu-experience'

const Message = withCitrusTheme(Box, () => ({
  '& p': {
    margin: 0,
  },
}))

const Label = withCitrusTheme('span', (theme) => ({
  fontFamily: theme.tokens.FontFamilies[FontFamily.Book],
  fontWeight: 400,
  '& strong': {
    fontFamily: theme.tokens.FontFamilies[FontFamily.Bold],
    fontWeight: 700,
  },
}))

const messageStyles = {
  note: {
    color: Color.ColdGrey_800,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  info: {
    color: Color.Informative_900,
    iconName: 'info',
    iconVariant: IconVariant.Informative,
  },
  warning: {
    color: Color.Warning_900,
    iconName: 'warning',
    iconVariant: IconVariant.Inherit,
  },
  error: {
    color: Color.ColdGrey_800,
    iconName: 'error',
    iconVariant: IconVariant.Error,
  },
  success: {
    color: Color.Success_900,
    iconName: 'success',
    iconVariant: IconVariant.Confirmation,
  },
} as const

type CheckoutButtonProps = {
  isOverLimitBy: number
  maxRecipesInBox: number
  onCheckout: (e: SyntheticEvent) => void
  isCheckoutDisabled: boolean
  isCheckoutPending: boolean
  discountLabel?: string | null
  message?: CheckoutMessage
  labelHTML?: string
}

export const CheckoutButton = ({
  isOverLimitBy,
  maxRecipesInBox,
  onCheckout,
  isCheckoutDisabled,
  isCheckoutPending,
  discountLabel,
  message,
  labelHTML,
}: CheckoutButtonProps) => {
  const isDisabled = isOverLimitBy > 0 || isCheckoutDisabled
  const serverMessageStyles = message ? messageStyles[message.displayType] : undefined

  return (
    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      alignItems={AlignItems.Center}
      bg={Color.White}
      paddingH={[4, 0]}
      width="100%"
    >
      {message && serverMessageStyles ? (
        <Box
          alignItems={AlignItems.Center}
          color={serverMessageStyles.color}
          data-testid={`checkout-message-${message.displayType}`}
          display={Display.Flex}
          gap={1}
          paddingBottom={3}
          role={message.displayType === 'error' ? 'alert' : 'status'}
        >
          <Icon name={serverMessageStyles.iconName} variant={serverMessageStyles.iconVariant} />
          <Message dangerouslySetInnerHTML={safeHtml(message.messageHTML)} />
        </Box>
      ) : isOverLimitBy > 0 ? (
        <Box display={Display.Flex} alignItems={AlignItems.Center} gap={1} paddingBottom={3}>
          <Icon name="error" variant={IconVariant.Error} />
          <Text color={Color.ColdGrey_800}>
            Max {maxRecipesInBox} recipes fit in a box. Remove <strong>{isOverLimitBy}</strong>{' '}
            recipes.
          </Text>
        </Box>
      ) : null}
      <ModalButton
        colorVariant={ButtonColorVariant.Primary}
        data-testid="checkout-button"
        size={ButtonSize.Large}
        onClick={onCheckout}
        disabled={isDisabled}
        height="48px"
        width={['100%', '400px']}
      >
        {isCheckoutPending ? (
          <Spinner color={Color.White} data-testid="checkout-spinner" size={6} />
        ) : labelHTML ? (
          <Label dangerouslySetInnerHTML={safeHtml(labelHTML)} />
        ) : discountLabel ? (
          `Checkout • ${discountLabel}`
        ) : (
          'Checkout'
        )}
      </ModalButton>
    </Box>
  )
}
